/*
  QUICK-VIEW FORM-SUBMIT SAFETY NET — Issue 2

  Closes a race in Stiletto's quick-view modal: when the user clicks
  the "Add to Bag" button inside the modal, the form submit event
  fires natively → form posts to /cart → page navigates to /cart.

  Why the race exists:
  --------------------
  Stiletto's quick-view flow (`assets/theme.js:6688`) loads the QV
  HTML via XHR and `innerHTML`-injects it. It then calls
  `new Product(container)`. Product's constructor in turn calls
  `getProduct(handle)` (theme.js:4343) — ANOTHER XHR fetch that
  resolves the product JSON. Only AFTER the second XHR resolves
  does `ProductForm(...)` get instantiated and its `submit` listener
  (theme.js:4961) get bound to the form.

  If the user clicks the ATC button BEFORE that second XHR resolves
  (common on local dev where requests are slower), the form has no
  JS submit handler. The browser does its native form submit and
  the page navigates to `/cart`.

  Fix:
  ----
  A document-level CAPTURE-phase submit listener that runs BEFORE
  any handler attached at the form level. Whenever the submit
  comes from a `[data-product-form]` inside a `.quick-product`
  wrapper, we:
    1. preventDefault() — stops the native /cart navigation.
    2. If Stiletto's handler is already bound, do nothing — its
       bubble-phase handler will run on the same event and call
       `cart.addItem(form)`.
    3. If Stiletto isn't bound yet, manually call `cart.addItem`
       via a direct fetch so the user's click still works.
       The cart drawer opens via a `quick-cart:open` PubSub emit
       that we replicate.

  Idempotent via `window.__qvFormGuardInit`. No-op if no quick view
  is on the page.
*/

(function () {
  if (window.__qvFormGuardInit) return;
  window.__qvFormGuardInit = true;

  /*
    Serialize a HTMLFormElement's data-bearing fields the same
    way Stiletto does (theme.js:4239 `serialize(form)`). Skips
    disabled, file, reset, submit, button inputs; respects
    select-multiple; encodes URL-safe.
  */
  function serializeForm(form) {
    var arr = [];
    Array.prototype.slice.call(form.elements).forEach(function (field) {
      if (!field.name || field.disabled || ['file', 'reset', 'submit', 'button'].indexOf(field.type) > -1) {
        return;
      }
      if (field.type === 'select-multiple') {
        Array.prototype.slice.call(field.options).forEach(function (option) {
          if (!option.selected) return;
          arr.push(encodeURIComponent(field.name) + '=' + encodeURIComponent(option.value));
        });
        return;
      }
      if (['checkbox', 'radio'].indexOf(field.type) > -1 && !field.checked) {
        return;
      }
      arr.push(encodeURIComponent(field.name) + '=' + encodeURIComponent(field.value));
    });
    return arr.join('&');
  }

  function fallbackAddToCart(form, button) {
    if (button) button.classList.add('loading');

    var addUrl = (window.theme && window.theme.routes && window.theme.routes.cart && window.theme.routes.cart.add)
      ? window.theme.routes.cart.add + '.js'
      : '/cart/add.js';

    return fetch(addUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: serializeForm(form)
    })
      .then(function (r) { return r.json(); })
      .then(function (item) {
        if (item && item.status === 422) {
          // Bubble up so any visible error UI catches it.
          document.dispatchEvent(new CustomEvent('cart:error', {
            detail: { errorMessage: item.description || 'Could not add to cart' }
          }));
          throw item;
        }

        // Refresh cart state so listeners that need the full cart
        // payload (cart-count badges, free-shipping bar, etc.) get
        // fresh totals.
        return fetch('/cart.js', { credentials: 'include' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            // 1) Notify document-level listeners. `assets/custom-events
            //    .js` logs and re-emits these; the header's `register
            //    ("header")` listens on `apps:product-added-to-cart`
            //    and refreshes the cart-count badge.
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
            document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: item } }));

            // 2) Trigger the quick-cart drawer's `refreshQuickCart`
            //    via the `apps:product-added-to-cart` event that the
            //    `register("quick-cart")` block subscribes to
            //    (theme.js:9300). This re-fetches `?section_id=quick
            //    -cart` and renders the new item INSIDE the drawer's
            //    `.quick-cart__wrapper`.
            document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));

            // 3) Close the quick-view modal so it isn't overlaying
            //    the cart drawer once it opens.
            var qvModal = document.querySelector('[data-quick-view-modal]');
            if (qvModal && qvModal.classList.contains('is-visible')) {
              var closeBtn = qvModal.querySelector('[data-modal-close]');
              if (closeBtn) closeBtn.click();
            }

            // 4) Open the cart drawer by simulating a click on
            //    `[data-js-cart-icon]`. This fires Stiletto's own
            //    handler (theme.js:8758) which emits `r$1("quick-cart
            //    :open")` on the internal pubsub — the only reliable
            //    way to invoke `openQuickCart` from outside the
            //    bundle. Toggling classes manually does NOT work:
            //    Stiletto adds `active` to `.quick-cart__wrapper`
            //    (not `.quick-cart`), and skipping `openQuickCart`
            //    skips `cartTrap.activate()`, body-scroll-lock, and
            //    the animation hooks. Wait one frame so the modal-
            //    close animation can start before the drawer opens.
            requestAnimationFrame(function () {
              var cartIcon = document.querySelector('[data-js-cart-icon]');
              if (cartIcon) cartIcon.click();
            });
          });
      })
      .catch(function (err) {
        // Already dispatched cart:error above for 422; nothing more to do.
        // Console.warn (not error) so it doesn't trip Sentry-style
        // global handlers — this is a soft-fallback path.
        console.warn('[qv-form-guard] fallback add-to-cart failed', err);
      })
      .then(function () {
        if (button) button.classList.remove('loading');
      });
  }

  /*
    Capture-phase submit listener. Runs BEFORE any form-level
    submit handler (Stiletto's listener is attached at form
    level). `useCapture = true` is what makes this run early —
    even at document level, capture phase precedes the bubbling
    target handlers so we can preventDefault before the native
    action.

    Strategy: claim the click ENTIRELY. Use stopImmediatePropagation
    so Stiletto's bubble-phase listener does NOT also run (and
    double-add the item). We replicate Stiletto's flow ourselves
    via fallbackAddToCart — fetch /cart/add.js, fetch /cart.js,
    fire cart events, and toggle the cart drawer. Net behavior
    is identical to Stiletto's path; the only difference is
    timing: our path doesn't need to wait for the productForm
    binding to complete.
  */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    // Only intercept the QV product form; PDP and other forms
    // are unaffected.
    if (!form.matches('[data-product-form]')) return;
    if (!form.closest('.quick-product')) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    // Reentrancy guard — if a second submit fires while a fetch
    // is still in flight, drop it.
    if (form.dataset.qvGuardFiring === '1') return;
    form.dataset.qvGuardFiring = '1';

    var button = form.querySelector('[data-add-to-cart]');
    fallbackAddToCart(form, button).then(function () {
      form.dataset.qvGuardFiring = '0';
    }, function () {
      form.dataset.qvGuardFiring = '0';
    });
  }, true);
})();
